/**
 * Custom hand order (FR-UX-04): the player moves picked cards along their hand with arrows.
 * Positions are in the hand's reading order, so moving past a row wrap is one step like any
 * other; nothing is dragged and nothing jitters at screen edges.
 */
export type Move = -1 | 1 | 'start' | 'end';

/** The hand in the saved custom order; cards not in it yet (new draws) go on the end in dealt order. */
export function applyOrder<T extends { readonly id: string }>(hand: readonly T[], order: readonly string[] | undefined): T[] {
  if (!order) return [...hand];
  const rank = new Map(order.map((id, i) => [id, i]));
  const known = hand.filter((c) => rank.has(c.id)).sort((a, b) => (rank.get(a.id) as number) - (rank.get(b.id) as number));
  return [...known, ...hand.filter((c) => !rank.has(c.id))];
}

/**
 * Move the picked cards one step (or to an end). Several picked cards are first pulled together
 * where the first of them sits, then travel as one block.
 */
export function moveBlock(order: readonly string[], picked: readonly string[], move: Move): string[] {
  const set = new Set(picked.filter((id) => order.includes(id)));
  if (set.size === 0) return [...order];
  const block = order.filter((id) => set.has(id));
  const rest = order.filter((id) => !set.has(id));
  const firstAt = order.findIndex((id) => set.has(id));
  // Index in `rest` where the block currently starts once it has been pulled together.
  const at = order.slice(0, firstAt).length;
  const contiguous = order.slice(firstAt, firstAt + block.length).every((id) => set.has(id));
  const target =
    move === 'start' ? 0 : move === 'end' ? rest.length : !contiguous ? at : Math.max(0, Math.min(rest.length, at + move));
  return [...rest.slice(0, target), ...block, ...rest.slice(target)];
}
